import React, { useState, useEffect, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  Edit2, Trash2, History, Save, Check, X, Clock, AlertCircle,
  RefreshCw, ChevronLeft, ChevronRight, Users, GraduationCap,
  Heart, Calendar, CheckSquare, AlertTriangle, Info, BarChart2,
  Download, Printer, FileText, UserCheck, StopCircle, PlayCircle,
  Pause, Ban, UserPlus, Star
} from 'lucide-react';
import { attendanceAPI, classesAPI, teachersAPI, volunteersAPI, settingsAPI } from '../services/api';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useActiveYear } from '../contexts/ActiveYearContext';
import DateInput from '../components/Dateinput';
import { MyOwnAttendanceRecords, TeacherAttendanceRecords, VolunteerAttendanceRecords } from './Attendancerecordsview';
import toast from 'react-hot-toast';

// ─── Helpers ──────────────────────────────────────────────────────
const getTodayIST = () => {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Kolkata', year: 'numeric', month: '2-digit', day: '2-digit'
  }).formatToParts(new Date());
  return `${parts.find(p => p.type === 'year').value}-${parts.find(p => p.type === 'month').value}-${parts.find(p => p.type === 'day').value}`;
};

const formatDisplayDate = (dateStr) => {
  if (!dateStr) return '';
  try {
    return new Date(dateStr).toLocaleDateString('en-IN', {
      timeZone: 'Asia/Kolkata', day: '2-digit', month: 'short', year: 'numeric', weekday: 'short'
    });
  } catch { return dateStr; }
};

const formatShortDate = (dateStr) => {
  if (!dateStr) return '';
  try {
    return new Date(dateStr).toLocaleDateString('en-IN', {
      timeZone: 'Asia/Kolkata', day: '2-digit', month: 'short', year: 'numeric'
    });
  } catch { return dateStr; }
};

// ─── Status Badge ──────────────────────────────────────────────────
const STATUS_MAP = {
  present: { bg: '#dcfce7', color: '#15803d', label: '✓ Present' },
  absent:  { bg: '#fee2e2', color: '#b91c1c', label: '✗ Absent' },
  late:    { bg: '#fef9c3', color: '#a16207', label: '⏰ Late' },
  leave:   { bg: '#ede9fe', color: '#6d28d9', label: '📋 Leave' },
  halfDay: { bg: '#ffedd5', color: '#c2410c', label: '½ Half Day' },
};
const StatusBadge = ({ status }) => {
  const s = STATUS_MAP[status] || { bg: '#f1f5f9', color: '#475569', label: status || '—' };
  return <span className="badge" style={{ background: s.bg, color: s.color }}>{s.label}</span>;
};

const RateBar = ({ rate }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
    <div style={{ width: 56, height: 5, background: 'var(--color-border)', borderRadius: 99, overflow: 'hidden' }}>
      <div style={{ width: `${rate}%`, height: '100%', borderRadius: 99, background: rate >= 80 ? '#16a34a' : rate >= 60 ? '#d97706' : '#dc2626', transition: 'width 0.4s' }} />
    </div>
    <span style={{ fontWeight: 700, fontSize: '0.82rem', color: rate >= 80 ? '#15803d' : rate >= 60 ? '#a16207' : '#b91c1c' }}>{rate}%</span>
  </div>
);

// ─── FIX 1: New Students Alert Banner ─────────────────────────────
function NewStudentsAlert({ existingRecord, currentStudents }) {
  if (!existingRecord || !currentStudents?.length) return null;

  const submittedStudentIds = new Set(
    (existingRecord.records || []).map(r => r.student?._id?.toString() || r.student?.toString())
  );
  const newStudents = currentStudents.filter(s => !submittedStudentIds.has(s._id?.toString()));

  if (newStudents.length === 0) return null;

  return (
    <div style={{
      background: 'linear-gradient(135deg, #fef3c7, #fef9e8)',
      border: '1.5px solid #fbbf24',
      borderRadius: 12,
      padding: '14px 18px',
      marginBottom: 16,
      display: 'flex',
      gap: 12,
      alignItems: 'flex-start',
    }}>
      <div style={{ width: 36, height: 36, borderRadius: 10, background: '#fef3c7', border: '1.5px solid #f59e0b', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        <UserPlus size={18} color="#d97706" />
      </div>
      <div style={{ flex: 1 }}>
        <div style={{ fontWeight: 800, fontSize: '0.875rem', color: '#92400e', marginBottom: 4 }}>
          {newStudents.length} New Student{newStudents.length > 1 ? 's' : ''} Added After Submission
        </div>
        <div style={{ fontSize: '0.78rem', color: '#a16207', lineHeight: 1.5, marginBottom: 8 }}>
          The following student{newStudents.length > 1 ? 's were' : ' was'} assigned to this class <strong>after</strong> attendance was already submitted. Their attendance is not recorded for this date. An admin can modify the record to include them.
        </div>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {newStudents.map(s => (
            <span key={s._id} style={{
              display: 'inline-flex', alignItems: 'center', gap: 4,
              padding: '3px 10px', borderRadius: 99,
              background: 'white', border: '1px solid #fbbf24',
              fontSize: '0.75rem', fontWeight: 700, color: '#92400e',
            }}>
              <Star size={10} fill="#f59e0b" color="#f59e0b" />
              {s.name}
              <span style={{ fontWeight: 400, color: '#a16207' }}>· {s.grade}</span>
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── Stop Window Modal ─────────────────────────────────────────────
function StopWindowModal({ isOpen, onClose, settings, onToggle, loading }) {
  const [reason, setReason] = useState('');
  const isStopped = settings?.timeWindow?.attendanceStopped;

  if (!isOpen) return null;

  const handleSubmit = () => {
    onToggle({ stop: !isStopped, reason: isStopped ? '' : reason });
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.6)', backdropFilter: 'blur(4px)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}
      onClick={onClose}>
      <div style={{ background: 'white', borderRadius: 20, width: '100%', maxWidth: 460, boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}
        onClick={e => e.stopPropagation()}>
        <div style={{ padding: '20px 24px', borderBottom: '1px solid var(--color-border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ width: 38, height: 38, borderRadius: 10, background: isStopped ? '#f0fdf4' : '#fef2f2', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              {isStopped ? <PlayCircle size={18} color="#16a34a" /> : <StopCircle size={18} color="#dc2626" />}
            </div>
            <span style={{ fontWeight: 700, fontSize: '0.95rem' }}>
              {isStopped ? 'Resume Attendance Window' : 'Stop Attendance Window'}
            </span>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-text-muted)', display: 'flex', padding: 4, borderRadius: 6 }}>
            <X size={18} />
          </button>
        </div>

        <div style={{ padding: '20px 24px' }}>
          {isStopped ? (
            <div>
              <div style={{ padding: '12px 16px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 12, marginBottom: 16 }}>
                <div style={{ fontSize: '0.8rem', fontWeight: 700, color: '#b91c1c', marginBottom: 4 }}>Currently Stopped</div>
                <div style={{ fontSize: '0.82rem', color: '#7f1d1d' }}>{settings?.timeWindow?.stopReason || 'No reason provided'}</div>
              </div>
              <p style={{ fontSize: '0.875rem', color: 'var(--color-text-secondary)', lineHeight: 1.6 }}>
                Resuming will allow teachers to submit attendance again within the configured time window.
              </p>
            </div>
          ) : (
            <div>
              <p style={{ fontSize: '0.875rem', color: 'var(--color-text-secondary)', lineHeight: 1.6, marginBottom: 16 }}>
                Stopping the window will prevent teachers from submitting attendance until it's resumed. Use this for <strong>leave days, holidays,</strong> or <strong>cancelled sessions</strong>.
              </p>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label">Reason <span style={{ color: 'var(--color-text-muted)', fontWeight: 400, fontSize: '0.75rem' }}>(recommended)</span></label>
                <textarea
                  className="form-textarea"
                  rows={3}
                  value={reason}
                  onChange={e => setReason(e.target.value)}
                  placeholder="e.g., Public holiday, Teacher meeting day, Rain holiday..."
                  style={{ resize: 'none' }}
                />
              </div>
            </div>
          )}
        </div>

        <div style={{ padding: '14px 24px', borderTop: '1px solid var(--color-border)', display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button className="btn btn-secondary" onClick={onClose} disabled={loading}>Cancel</button>
          <button
            className={`btn ${isStopped ? 'btn-success' : 'btn-danger'}`}
            onClick={handleSubmit}
            disabled={loading}
          >
            {loading
              ? <><span className="spinner" style={{ width: 14, height: 14, borderWidth: 2 }} /> Processing…</>
              : isStopped
                ? <><PlayCircle size={15} /> Resume Window</>
                : <><StopCircle size={15} /> Stop Window</>
            }
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Window Status Banner ──────────────────────────────────────────
function WindowBanner({ user, activeSettingsId, activeSettings }) {
  const qc = useQueryClient();
  const [showStopModal, setShowStopModal] = useState(false);

  const { data: windowData, refetch } = useQuery({
    queryKey: ['window-status'],
    queryFn: () => attendanceAPI.getWindowStatus().then(r => r.data?.data),
    refetchInterval: 60000,
  });

  const toggleMut = useMutation({
    mutationFn: (data) => api.put(`/settings/${activeSettingsId}/toggle-window`, data),
    onSuccess: (res) => {
      qc.invalidateQueries(['window-status']);
      qc.invalidateQueries(['active-settings']);
      toast.success(res.data?.message || 'Window updated');
      setShowStopModal(false);
    },
    onError: (err) => toast.error(err.response?.data?.message || 'Failed to update window'),
  });

  const isStopped = windowData?.stopped;
  const isAdmin = user?.role === 'admin';

  return (
    <>
      <StopWindowModal
        isOpen={showStopModal}
        onClose={() => setShowStopModal(false)}
        settings={activeSettings}
        onToggle={(data) => toggleMut.mutate(data)}
        loading={toggleMut.isPending}
      />

      <div style={{
        display: 'flex', alignItems: 'center', gap: 12, padding: '10px 16px',
        borderRadius: 10, marginBottom: 16, flexWrap: 'wrap',
        background: isStopped ? '#fff7ed' : windowData?.allowed ? '#f0fdf4' : '#fef2f2',
        border: `1px solid ${isStopped ? '#fed7aa' : windowData?.allowed ? '#bbf7d0' : '#fecaca'}`,
      }}>
        <span style={{
          padding: '3px 10px', borderRadius: 99, fontSize: '0.72rem', fontWeight: 800,
          letterSpacing: '0.05em', textTransform: 'uppercase',
          background: isStopped ? '#ea580c' : windowData?.allowed ? '#16a34a' : '#dc2626',
          color: 'white', display: 'flex', alignItems: 'center', gap: 5,
        }}>
          {isStopped ? <><Ban size={11} /> Stopped</> : windowData?.allowed ? 'Window OPEN' : 'Window CLOSED'}
        </span>

        <span style={{ fontSize: '0.82rem', color: 'var(--color-text-secondary)', flex: 1 }}>
          {windowData?.message || 'Loading...'}
        </span>

        {windowData?.allowed && windowData?.minutesRemaining > 0 && !isStopped && (
          <span style={{ fontSize: '0.82rem', fontWeight: 700, color: '#92400e', background: '#fef3c7', padding: '3px 10px', borderRadius: 99 }}>
            <Clock size={13} style={{ marginRight: 4, verticalAlign: 'middle' }} />
            {windowData.minutesRemaining} min remaining
          </span>
        )}

        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <button onClick={() => refetch()} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-text-muted)', display: 'flex' }}>
            <RefreshCw size={14} />
          </button>

          {isAdmin && activeSettingsId && (
            <button
              onClick={() => setShowStopModal(true)}
              style={{
                display: 'flex', alignItems: 'center', gap: 6, padding: '5px 12px',
                borderRadius: 8, border: `1.5px solid ${isStopped ? '#16a34a' : '#dc2626'}`,
                background: isStopped ? '#f0fdf4' : '#fef2f2',
                color: isStopped ? '#15803d' : '#b91c1c',
                cursor: 'pointer', fontFamily: 'var(--font-sans)',
                fontSize: '0.75rem', fontWeight: 700,
              }}
            >
              {isStopped ? <><PlayCircle size={13} /> Resume</> : <><Pause size={13} /> Stop Window</>}
            </button>
          )}
        </div>
      </div>
    </>
  );
}

// ─── Pending Classes Panel ─────────────────────────────────────────
function PendingClassesPanel({ date, vbsYear, classes, submittedRecords }) {
  const submittedClassIds = new Set((submittedRecords || []).map(r => r.class?._id?.toString() || r.class?.toString()));
  const pendingClasses = (classes || []).filter(c => !submittedClassIds.has(c._id?.toString()));

  if (pendingClasses.length === 0) {
    return (
      <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 12, padding: '14px 18px', display: 'flex', alignItems: 'center', gap: 10 }}>
        <CheckSquare size={18} color="#16a34a" />
        <div>
          <div style={{ fontWeight: 700, color: '#15803d', fontSize: '0.875rem' }}>All classes submitted!</div>
          <div style={{ color: '#166534', fontSize: '0.78rem', marginTop: 2 }}>{formatDisplayDate(date)} — {(classes || []).length} classes</div>
        </div>
      </div>
    );
  }

  const CATEGORY_COLORS = { Beginner: '#ede9fe', Primary: '#dbeafe', Junior: '#dcfce7', Inter: '#fef9c3' };
  const CATEGORY_TEXT  = { Beginner: '#5b21b6', Primary: '#1d4ed8', Junior: '#15803d', Inter: '#92400e' };

  return (
    <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 12, padding: '14px 18px', marginBottom: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <AlertTriangle size={16} color="#d97706" />
          <span style={{ fontWeight: 700, color: '#92400e', fontSize: '0.875rem' }}>
            {pendingClasses.length} class{pendingClasses.length > 1 ? 'es' : ''} pending
          </span>
          <span style={{ fontSize: '0.75rem', color: '#a16207', background: '#fef3c7', padding: '2px 8px', borderRadius: 99, fontWeight: 600 }}>
            {formatShortDate(date)}
          </span>
        </div>
        <span style={{ fontSize: '0.72rem', color: '#a16207' }}>
          {(submittedRecords || []).length}/{(classes || []).length} submitted
        </span>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 8 }}>
        {pendingClasses.map(cls => (
          <div key={cls._id} style={{ background: 'white', border: '1px solid #fde68a', borderRadius: 10, padding: '9px 12px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6 }}>
              <div style={{ fontWeight: 700, fontSize: '0.82rem', color: 'var(--color-text)' }}>{cls.name}</div>
              <span style={{ fontSize: '0.62rem', fontWeight: 800, padding: '2px 7px', borderRadius: 99, background: CATEGORY_COLORS[cls.category] || '#f1f5f9', color: CATEGORY_TEXT[cls.category] || '#475569' }}>
                {cls.category}
              </span>
            </div>
            <div style={{ fontSize: '0.73rem', color: 'var(--color-text-muted)', marginTop: 3 }}>
              {cls.teacher?.name || 'No teacher'}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Teacher: Mark Student Attendance ─────────────────────────────
function TeacherMarkAttendance() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [date, setDate] = useState(getTodayIST());
  const [records, setRecords] = useState({});
  const todayIST = getTodayIST();

  const { data: windowData } = useQuery({
    queryKey: ['window-status'],
    queryFn: () => attendanceAPI.getWindowStatus().then(r => r.data?.data),
    refetchInterval: 60000,
  });

  const { data: activeSettings } = useQuery({
    queryKey: ['active-settings-teacher'],
    queryFn: () => settingsAPI.getActive().then(r => r.data?.data),
  });

  const { data: classData, isLoading: loadingClass } = useQuery({
    queryKey: ['my-class-full', user._id],
    queryFn: async () => {
      const { data: tData } = await teachersAPI.getAll();
      const teacher = tData.data?.find(t =>
        t.user?._id?.toString() === user._id?.toString() ||
        t.user?.toString() === user._id?.toString()
      );
      if (!teacher?.classAssigned?._id && !teacher?.classAssigned) return null;
      const classId = teacher.classAssigned?._id || teacher.classAssigned;
      const { data: clsData } = await classesAPI.getOne(classId);
      return { ...clsData.data, teacherName: teacher.name };
    },
  });

  const { data: existingRecord, isLoading: checkingExisting } = useQuery({
    queryKey: ['attendance-check', date, classData?._id],
    queryFn: () => attendanceAPI.getStudentAttendance({ date, classId: classData._id }).then(r => r.data?.data?.[0]),
    enabled: !!classData?._id,
  });

  const submitMutation = useMutation({
    mutationFn: (data) => attendanceAPI.submitStudentAttendance(data),
    onSuccess: () => {
      toast.success('Attendance submitted!');
      qc.invalidateQueries(['attendance-check']);
      qc.invalidateQueries(['teacher-history']);
      setRecords({});
    },
    onError: (err) => toast.error(err.response?.data?.message || 'Submission failed'),
  });

  if (loadingClass) return <div className="loading-center"><div className="spinner" /></div>;

  if (!classData) return (
    <div style={{ textAlign: 'center', padding: 56, maxWidth: 420, margin: '0 auto' }}>
      <div style={{ width: 64, height: 64, borderRadius: 16, background: 'var(--color-bg)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
        <Users size={32} color="var(--color-text-muted)" />
      </div>
      <h3 style={{ fontWeight: 700, marginBottom: 8 }}>No Class Assigned</h3>
      <p style={{ color: 'var(--color-text-secondary)', fontSize: '0.875rem' }}>Contact admin to get a class assigned.</p>
    </div>
  );

  const students = classData.students || [];
  const alreadySubmitted = !!existingRecord;
  const isToday = date === todayIST;
  const windowStopped = windowData?.stopped;
  const windowOpen = windowData?.allowed && !windowStopped;
  const canSubmit = isToday && windowOpen && !alreadySubmitted;

  const markedCount  = Object.keys(records).length;
  const presentCount = Object.values(records).filter(v => v === 'present').length;
  const unmarked     = students.length - markedCount;
  const markAll      = (status) => { const all = {}; students.forEach(s => { all[s._id] = status; }); setRecords(all); };

  return (
    <div style={{ maxWidth: '100%' }}>
      {windowStopped && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px', borderRadius: 10, marginBottom: 16, background: '#fff7ed', border: '1px solid #fed7aa' }}>
          <Ban size={16} color="#ea580c" />
          <div>
            <div style={{ fontWeight: 700, fontSize: '0.845rem', color: '#9a3412' }}>Attendance Window Stopped</div>
            <div style={{ fontSize: '0.78rem', color: '#c2410c', marginTop: 2 }}>{windowData?.message?.replace('⛔ ', '')}</div>
          </div>
        </div>
      )}
      {!windowStopped && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 12, padding: '10px 16px',
          borderRadius: 10, marginBottom: 16,
          background: windowOpen ? '#f0fdf4' : '#fef2f2',
          border: `1px solid ${windowOpen ? '#bbf7d0' : '#fecaca'}`,
        }}>
          <span style={{ padding: '3px 10px', borderRadius: 99, fontSize: '0.72rem', fontWeight: 800, background: windowOpen ? '#16a34a' : '#dc2626', color: 'white' }}>
            {windowOpen ? 'Window OPEN' : 'Window CLOSED'}
          </span>
          <span style={{ fontSize: '0.82rem', color: 'var(--color-text-secondary)', flex: 1 }}>{windowData?.message || 'Loading...'}</span>
          {windowOpen && windowData?.minutesRemaining > 0 && (
            <span style={{ fontSize: '0.82rem', fontWeight: 700, color: '#92400e', background: '#fef3c7', padding: '3px 10px', borderRadius: 99 }}>
              <Clock size={13} style={{ marginRight: 4, verticalAlign: 'middle' }} />
              {windowData.minutesRemaining} min
            </span>
          )}
        </div>
      )}

      <div style={{ display: 'flex', gap: 12, marginBottom: 16, alignItems: 'flex-end', flexWrap: 'wrap' }}>
        <div style={{ flex: '1 1 200px', minWidth: 180 }}>
          <DateInput label="Attendance Date" value={date} onChange={(v) => { setDate(v); setRecords({}); }}
            max={todayIST}
            vbsStartDate={activeSettings?.dates?.startDate?.slice(0, 10)}
            vbsEndDate={activeSettings?.dates?.endDate?.slice(0, 10)}
            showVBSDays={true} />
        </div>
        <div style={{ flex: 2, padding: '8px 14px', background: 'var(--color-bg)', borderRadius: 10, border: '1px solid var(--color-border)', minWidth: 0 }}>
          <div style={{ fontSize: '0.72rem', color: 'var(--color-text-muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Class</div>
          <div style={{ fontWeight: 700, marginTop: 2 }}>
            {classData.name}{' '}
            <span style={{ fontWeight: 400, color: 'var(--color-text-secondary)', fontSize: '0.82rem' }}>· {classData.category} · {students.length} students</span>
          </div>
        </div>
      </div>

      {alreadySubmitted && (
        <NewStudentsAlert existingRecord={existingRecord} currentStudents={students} />
      )}

      {alreadySubmitted && (
        <div className="alert alert-success" style={{ marginBottom: 16 }}>
          <Check size={15} style={{ flexShrink: 0 }} />
          <div>Attendance submitted for <strong>{formatDisplayDate(date)}</strong>.</div>
        </div>
      )}
      {!isToday && !alreadySubmitted && !checkingExisting && (
        <div className="alert alert-warning" style={{ marginBottom: 16 }}>
          <AlertCircle size={15} style={{ flexShrink: 0 }} />
          <div>No record for <strong>{formatDisplayDate(date)}</strong>. Submit only for today within the open window.</div>
        </div>
      )}
      {isToday && !windowOpen && !alreadySubmitted && !windowStopped && (
        <div className="alert alert-warning" style={{ marginBottom: 16 }}>
          <Clock size={15} style={{ flexShrink: 0 }} />
          <div>Window is <strong>closed</strong>. Contact admin for late submissions.</div>
        </div>
      )}

      {canSubmit && (
        <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
          <button className="btn btn-secondary btn-sm" onClick={() => markAll('present')}><Check size={14} /> All Present</button>
          <button className="btn btn-secondary btn-sm" onClick={() => markAll('absent')}><X size={14} /> All Absent</button>
          <button className="btn btn-ghost btn-sm" onClick={() => setRecords({})}><RefreshCw size={14} /> Clear</button>
          {markedCount > 0 && (
            <span style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.82rem', padding: '4px 12px', background: 'var(--color-bg)', borderRadius: 8, border: '1px solid var(--color-border)' }}>
              <span style={{ color: '#16a34a', fontWeight: 700 }}>✓ {presentCount}</span>
              <span style={{ color: '#dc2626', fontWeight: 700 }}>✗ {markedCount - presentCount}</span>
              {unmarked > 0 && <span style={{ color: 'var(--color-text-muted)' }}>{unmarked} left</span>}
            </span>
          )}
        </div>
      )}

      <div className="card">
        <div className="card-header">
          <span className="card-title">{classData.name} — {formatDisplayDate(date)}</span>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ minWidth: 400 }}>
            <thead>
              <tr>
                <th style={{ width: 36 }}>#</th>
                <th>Name</th>
                <th style={{ width: 70 }}>Grade</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {students.map((s, idx) => {
                const submittedStatus = existingRecord?.records?.find(
                  r => r.student?._id?.toString() === s._id?.toString() || r.student?.toString() === s._id?.toString()
                )?.status;

                const isNewStudent = alreadySubmitted && !submittedStatus;
                const currentStatus = alreadySubmitted ? submittedStatus : records[s._id];

                return (
                  <tr key={s._id} style={{
                    background: isNewStudent
                      ? 'linear-gradient(135deg, #fefce8, #fef9c3)'
                      : canSubmit && records[s._id] ? 'rgba(26,47,94,0.02)' : undefined,
                    borderLeft: isNewStudent ? '3px solid #fbbf24' : undefined,
                  }}>
                    <td style={{ color: 'var(--color-text-muted)', fontSize: '0.78rem' }}>{idx + 1}</td>
                    <td style={{ fontWeight: 600, fontSize: '0.875rem' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        {s.name}
                        {isNewStudent && (
                          <span style={{
                            display: 'inline-flex', alignItems: 'center', gap: 3,
                            padding: '1px 7px', borderRadius: 99,
                            background: '#fef3c7', border: '1px solid #fbbf24',
                            fontSize: '0.62rem', fontWeight: 800, color: '#92400e',
                          }}>
                            <Star size={8} fill="#f59e0b" color="#f59e0b" /> NEW
                          </span>
                        )}
                      </div>
                    </td>
                    <td style={{ fontSize: '0.82rem', color: 'var(--color-text-secondary)' }}>
                      {['PreKG', 'LKG', 'UKG'].includes(s.grade) ? s.grade : `Std ${s.grade}`}
                    </td>
                    <td>
                      {isNewStudent ? (
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '3px 10px', borderRadius: 99, background: '#fef3c7', color: '#92400e', fontSize: '0.73rem', fontWeight: 700 }}>
                          ⚠ Not Recorded
                        </span>
                      ) : alreadySubmitted ? (
                        <StatusBadge status={currentStatus} />
                      ) : canSubmit ? (
                        <div style={{ display: 'flex', gap: 5 }}>
                          {['present', 'absent'].map(st => (
                            <button key={st} onClick={() => setRecords(r => ({ ...r, [s._id]: st }))}
                              style={{
                                padding: '4px 10px', borderRadius: 7, border: 'none', cursor: 'pointer',
                                fontWeight: 700, fontSize: '0.75rem', transition: 'all 0.12s',
                                background: currentStatus === st ? (st === 'present' ? '#16a34a' : '#dc2626') : 'var(--color-bg)',
                                color: currentStatus === st ? 'white' : 'var(--color-text-secondary)',
                              }}>
                              {st === 'present' ? '✓' : '✗'}
                            </button>
                          ))}
                        </div>
                      ) : (
                        <span style={{ color: 'var(--color-text-muted)', fontSize: '0.78rem' }}>—</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {canSubmit && (
          <div style={{ padding: '12px 16px', borderTop: '1px solid var(--color-border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10 }}>
            <div style={{ fontSize: '0.8rem', color: 'var(--color-text-secondary)' }}>
              {unmarked > 0 ? <span style={{ color: '#d97706' }}>⚠️ {unmarked} unmarked → Absent</span> : <span style={{ color: '#16a34a' }}>✓ All {students.length} marked</span>}
            </div>
            <button className="btn btn-primary" onClick={() => {
              const recs = students.map(s => ({ studentId: s._id, status: records[s._id] || 'absent' }));
              submitMutation.mutate({ date, classId: classData._id, records: recs });
            }} disabled={submitMutation.isPending}>
              {submitMutation.isPending ? <><div className="spinner" style={{ width: 14, height: 14, borderWidth: 2 }} /> Submitting...</> : <><Save size={15} /> Submit</>}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Teacher History ───────────────────────────────────────────────
function TeacherAttendanceHistory() {
  const { user } = useAuth();
  const [page, setPage] = useState(1);
  const pageSize = 10;

  const { data: classData } = useQuery({
    queryKey: ['my-class-full', user._id],
    queryFn: async () => {
      const { data: tData } = await teachersAPI.getAll();
      const teacher = tData.data?.find(t =>
        t.user?._id?.toString() === user._id?.toString() || t.user?.toString() === user._id?.toString()
      );
      if (!teacher?.classAssigned?._id && !teacher?.classAssigned) return null;
      const classId = teacher.classAssigned?._id || teacher.classAssigned;
      const { data: clsData } = await classesAPI.getOne(classId);
      return clsData.data;
    },
  });

  const { data: history, isLoading } = useQuery({
    queryKey: ['teacher-history', classData?._id],
    queryFn: () => attendanceAPI.getStudentAttendance({ classId: classData._id }).then(r => r.data?.data || []),
    enabled: !!classData?._id,
  });

  if (isLoading) return <div className="loading-center"><div className="spinner" /></div>;
  const sortedAsc  = [...(history || [])].sort((a, b) => new Date(a.date) - new Date(b.date));
  const totalRecords = sortedAsc.length;
  const dayNumberMap = {};
  sortedAsc.forEach((rec, idx) => { dayNumberMap[rec._id] = idx + 1; });
  const sortedDesc = [...sortedAsc].reverse();
  const totalPages = Math.ceil(sortedDesc.length / pageSize);
  const paged = sortedDesc.slice((page - 1) * pageSize, page * pageSize);

  if (totalRecords === 0) return (
    <div className="card" style={{ textAlign: 'center', padding: 48 }}>
      <Calendar size={36} style={{ color: 'var(--color-text-muted)', marginBottom: 12 }} />
      <h3>No attendance records yet</h3>
    </div>
  );

  return (
    <div>
      <div style={{ marginBottom: 14, fontSize: '0.85rem', color: 'var(--color-text-secondary)' }}>
        {totalRecords} records for {classData?.name}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {paged.map(rec => {
          const present = rec.records?.filter(r => r.status === 'present').length || 0;
          const absent  = rec.records?.filter(r => r.status === 'absent').length || 0;
          const total   = present + absent;
          const rate    = total > 0 ? Math.round((present / total) * 100) : 0;
          return (
            <div key={rec._id} className="card" style={{ padding: '14px 16px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                    <span className="badge badge-navy">Day {dayNumberMap[rec._id]}</span>
                    <span style={{ fontWeight: 700, fontSize: '0.875rem' }}>{formatDisplayDate(rec.date)}</span>
                  </div>
                  <div style={{ fontSize: '0.78rem', color: 'var(--color-text-secondary)' }}>
                    By {rec.submittedByName} · {rec.isModified ? <span style={{ color: '#c2410c', fontWeight: 700 }}>Modified</span> : <span style={{ color: '#16a34a' }}>Original</span>}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '1.2rem', fontWeight: 800, color: '#16a34a' }}>{present}</div>
                    <div style={{ fontSize: '0.62rem', color: 'var(--color-text-muted)', fontWeight: 600 }}>PRESENT</div>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '1.2rem', fontWeight: 800, color: '#dc2626' }}>{absent}</div>
                    <div style={{ fontSize: '0.62rem', color: 'var(--color-text-muted)', fontWeight: 600 }}>ABSENT</div>
                  </div>
                  <RateBar rate={rate} />
                </div>
              </div>
            </div>
          );
        })}
      </div>
      {totalPages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', gap: 6, marginTop: 16 }}>
          <button className="btn btn-secondary btn-sm" onClick={() => setPage(p => p - 1)} disabled={page <= 1}><ChevronLeft size={14} /></button>
          {Array.from({ length: totalPages }, (_, i) => i + 1).map(p => (
            <button key={p} className={`btn btn-sm ${p === page ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setPage(p)} style={{ minWidth: 32, justifyContent: 'center' }}>{p}</button>
          ))}
          <button className="btn btn-secondary btn-sm" onClick={() => setPage(p => p + 1)} disabled={page >= totalPages}><ChevronRight size={14} /></button>
        </div>
      )}
    </div>
  );
}

// ─── Staff Attendance Panel ─────────────────────────────────────────
function StaffAttendancePanel({ type }) {
  const qc = useQueryClient();
  const [date, setDate] = useState(getTodayIST());
  const [statuses, setStatuses] = useState({});
  const [times, setTimes] = useState({});
  const [remarks, setRemarks] = useState({});
  const [expandedId, setExpandedId] = useState(null);
  const todayIST  = getTodayIST();
  const isTeacher = type === 'teacher';

  const { data: activeSettings } = useQuery({
    queryKey: ['active-settings-staff'],
    queryFn: () => settingsAPI.getActive().then(r => r.data?.data),
  });

  const { data: entities, isLoading } = useQuery({
    queryKey: [`${type}s-list`],
    queryFn: () => isTeacher
      ? teachersAPI.getAll({ isActive: true }).then(r => r.data?.data || [])
      : volunteersAPI.getAll({ isActive: true }).then(r => r.data?.data || []),
  });

  const { data: existingRecords } = useQuery({
    queryKey: [`${type}-attendance-date`, date],
    queryFn: () => isTeacher
      ? attendanceAPI.getTeacherAttendance({ date }).then(r => r.data?.data || [])
      : attendanceAPI.getVolunteerAttendance({ date }).then(r => r.data?.data || []),
    select: data => {
      const map = {};
      data.forEach(rec => {
        const id = isTeacher
          ? rec.teacher?._id?.toString() || rec.teacher?.toString()
          : rec.volunteer?._id?.toString() || rec.volunteer?.toString();
        if (id) map[id] = rec;
      });
      return map;
    }
  });

  useEffect(() => {
    if (existingRecords) {
      const newStatuses = {};
      Object.entries(existingRecords).forEach(([id, rec]) => { newStatuses[id] = rec.status; });
      setStatuses(newStatuses);
    } else { setStatuses({}); }
    setExpandedId(null);
  }, [existingRecords, date]);

  const submitMutation = useMutation({
    mutationFn: (data) => isTeacher
      ? attendanceAPI.submitTeacherAttendance(data)
      : attendanceAPI.submitVolunteerAttendance(data),
    onSuccess: (res) => {
      qc.invalidateQueries([`${type}-attendance-date`]);
      const { created, updated } = res.data?.data || {};
      toast.success(`${(created?.length || 0) + (updated?.length || 0)} records saved`);
    },
    onError: (err) => toast.error(err.response?.data?.message || 'Failed'),
  });

  const TEACHER_OPTIONS = [
    { val: 'present', label: 'Present', short: 'P', color: '#16a34a', bg: '#dcfce7', border: '#86efac' },
    { val: 'absent',  label: 'Absent',  short: 'A', color: '#dc2626', bg: '#fee2e2', border: '#fca5a5' },
    { val: 'late',    label: 'Late',    short: 'L', color: '#d97706', bg: '#fef9c3', border: '#fde68a' },
    { val: 'leave',   label: 'Leave',   short: 'Lv', color: '#7c3aed', bg: '#ede9fe', border: '#c4b5fd' },
  ];
  const VOLUNTEER_OPTIONS = [
    { val: 'present', label: 'Present',  short: 'P',  color: '#16a34a', bg: '#dcfce7', border: '#86efac' },
    { val: 'absent',  label: 'Absent',   short: 'A',  color: '#dc2626', bg: '#fee2e2', border: '#fca5a5' },
    { val: 'halfDay', label: 'Half Day', short: '½',  color: '#c2410c', bg: '#ffedd5', border: '#fdba74' },
    { val: 'late',    label: 'Late',     short: 'L',  color: '#d97706', bg: '#fef9c3', border: '#fde68a' },
  ];
  const OPTIONS = isTeacher ? TEACHER_OPTIONS : VOLUNTEER_OPTIONS;

  const markAll = (status) => {
    const all = {};
    (entities || []).forEach(e => { all[e._id] = status; });
    setStatuses(all);
  };

  const handleSubmit = () => {
    const recs = (entities || []).filter(e => statuses[e._id]).map(e => ({
      [isTeacher ? 'teacherId' : 'volunteerId']: e._id,
      status: statuses[e._id],
      ...(isTeacher && times[e._id]?.arrival   ? { arrivalTime:   times[e._id].arrival }   : {}),
      ...(isTeacher && times[e._id]?.departure  ? { departureTime: times[e._id].departure }  : {}),
      ...(!isTeacher && times[e._id]?.checkIn   ? { checkInTime:   times[e._id].checkIn }    : {}),
      ...(!isTeacher && times[e._id]?.checkOut  ? { checkOutTime:  times[e._id].checkOut }   : {}),
      ...(remarks[e._id] ? { remarks: remarks[e._id] } : {}),
    }));
    if (!recs.length) { toast.error('Mark at least one person'); return; }
    submitMutation.mutate({ date, records: recs });
  };

  if (isLoading) return <div className="loading-center"><div className="spinner" /></div>;

  const entityList   = entities || [];
  const markedCount  = Object.keys(statuses).length;
  const presentCount = Object.values(statuses).filter(s => ['present', 'halfDay'].includes(s)).length;
  const absentCount  = Object.values(statuses).filter(s => s === 'absent').length;
  const lateCount    = Object.values(statuses).filter(s => s === 'late').length;

  return (
    <div>
      <div style={{ display: 'flex', gap: 10, marginBottom: 16, alignItems: 'flex-end', flexWrap: 'wrap' }}>
        <div style={{ flex: '1 1 180px', minWidth: 180 }}>
          <DateInput label="Date" value={date} onChange={setDate} max={todayIST}
            vbsStartDate={activeSettings?.dates?.startDate?.slice(0, 10)}
            vbsEndDate={activeSettings?.dates?.endDate?.slice(0, 10)}
            showVBSDays={true} />
        </div>
        <div style={{ display: 'flex', gap: 8, paddingBottom: 1, flexWrap: 'wrap' }}>
          <button className="btn btn-secondary btn-sm" onClick={() => markAll('present')}><Check size={14} /> All Present</button>
          <button className="btn btn-secondary btn-sm" onClick={() => markAll('absent')}><X size={14} /> All Absent</button>
          <button className="btn btn-ghost btn-sm" onClick={() => setStatuses({})}><RefreshCw size={14} /> Clear</button>
        </div>
        <button className="btn btn-primary btn-sm" onClick={handleSubmit} disabled={submitMutation.isPending} style={{ marginLeft: 'auto', marginBottom: 1 }}>
          {submitMutation.isPending ? <><div className="spinner" style={{ width: 14, height: 14, borderWidth: 2 }} /> Saving...</> : <><Save size={14} /> Save</>}
        </button>
      </div>

      {markedCount > 0 && (
        <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
          {[
            { label: 'Present', count: presentCount, color: '#15803d', bg: '#dcfce7' },
            { label: 'Absent',  count: absentCount,  color: '#b91c1c', bg: '#fee2e2' },
            { label: 'Late',    count: lateCount,     color: '#a16207', bg: '#fef9c3' },
            { label: 'Unmarked', count: entityList.length - markedCount, color: '#475569', bg: '#f1f5f9' },
          ].filter(s => s.count > 0).map(s => (
            <div key={s.label} style={{ padding: '5px 12px', borderRadius: 8, background: s.bg, fontSize: '0.78rem', fontWeight: 700, color: s.color }}>
              {s.count} {s.label}
            </div>
          ))}
        </div>
      )}

      <div style={{ border: '1px solid var(--color-border)', borderRadius: 12, overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: 'var(--color-bg)' }}>
              <th style={{ padding: '10px 16px', textAlign: 'left', fontSize: '0.7rem', fontWeight: 700, color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.07em', borderBottom: '2px solid var(--color-border)' }}>
                {isTeacher ? 'Teacher' : 'Volunteer'}
              </th>
              <th style={{ padding: '10px 12px', textAlign: 'left', fontSize: '0.7rem', fontWeight: 700, color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.07em', borderBottom: '2px solid var(--color-border)' }}>
                {isTeacher ? 'Class' : 'Role'}
              </th>
              <th style={{ padding: '10px 12px', textAlign: 'left', fontSize: '0.7rem', fontWeight: 700, color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.07em', borderBottom: '2px solid var(--color-border)' }}>
                Status
              </th>
              <th style={{ padding: '10px 12px', textAlign: 'right', fontSize: '0.7rem', fontWeight: 700, color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.07em', borderBottom: '2px solid var(--color-border)' }}>
                Details
              </th>
            </tr>
          </thead>
          <tbody>
            {entityList.map((e, idx) => {
              const existing      = existingRecords?.[e._id];
              const currentStatus = statuses[e._id];
              const isExpanded    = expandedId === e._id;

              return (
                <React.Fragment key={e._id}>
                  <tr style={{
                    borderTop: idx === 0 ? 'none' : '1px solid var(--color-border-light)',
                    background: isExpanded ? '#fafbff' : 'white',
                    transition: 'background 0.15s',
                  }}>
                    <td style={{ padding: '12px 16px', verticalAlign: 'middle' }}>
                      <div style={{ fontWeight: 600, fontSize: '0.875rem' }}>{e.name}</div>
                      <div style={{ fontSize: '0.72rem', color: 'var(--color-text-muted)', marginTop: 2 }}>
                        {e.contactNumber}
                        {existing && <span style={{ marginLeft: 6, color: '#16a34a', fontWeight: 700 }}>· Saved</span>}
                      </div>
                    </td>
                    <td style={{ padding: '12px', verticalAlign: 'middle' }}>
                      <span style={{ fontSize: '0.82rem', color: 'var(--color-text-secondary)' }}>
                        {isTeacher
                          ? (e.classAssigned?.name || <span style={{ color: 'var(--color-text-muted)' }}>Unassigned</span>)
                          : e.role}
                      </span>
                      {!isTeacher && e.shift && (
                        <div style={{ fontSize: '0.7rem', color: 'var(--color-text-muted)', marginTop: 2 }}>{e.shift}</div>
                      )}
                    </td>
                    <td style={{ padding: '8px 12px', verticalAlign: 'middle' }}>
                      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                        {OPTIONS.map(opt => (
                          <button key={opt.val}
                            onClick={() => setStatuses(s => ({ ...s, [e._id]: opt.val }))}
                            style={{
                              padding: '4px 10px', borderRadius: 7, cursor: 'pointer',
                              fontSize: '0.72rem', fontWeight: 700, transition: 'all 0.12s',
                              border: `1.5px solid ${currentStatus === opt.val ? opt.border : 'var(--color-border)'}`,
                              background: currentStatus === opt.val ? opt.bg : 'white',
                              color: currentStatus === opt.val ? opt.color : 'var(--color-text-muted)',
                            }}>
                            {opt.short} {opt.label}
                          </button>
                        ))}
                      </div>
                    </td>
                    <td style={{ padding: '8px 16px', verticalAlign: 'middle', textAlign: 'right' }}>
                      <button
                        onClick={() => setExpandedId(isExpanded ? null : e._id)}
                        style={{
                          padding: '5px 10px', borderRadius: 7, border: '1.5px solid var(--color-border)',
                          background: isExpanded ? 'var(--color-bg)' : 'white',
                          cursor: 'pointer', fontSize: '0.72rem', fontWeight: 600,
                          color: 'var(--color-text-secondary)', display: 'flex', alignItems: 'center', gap: 5,
                          fontFamily: 'var(--font-sans)',
                        }}>
                        {isExpanded ? <ChevronLeft size={12} /> : '+ Times / Remarks'}
                      </button>
                    </td>
                  </tr>

                  {isExpanded && (
                    <tr style={{ borderTop: '1px dashed var(--color-border)', background: '#f8fafd' }}>
                      <td colSpan={4} style={{ padding: '12px 16px 14px' }}>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 10 }}>
                          {isTeacher ? (
                            <>
                              <div>
                                <label style={{ display: 'block', fontSize: '0.68rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--color-text-muted)', marginBottom: 5 }}>Arrival Time</label>
                                <input type="time" value={times[e._id]?.arrival || ''}
                                  onChange={e2 => setTimes(t => ({ ...t, [e._id]: { ...t[e._id], arrival: e2.target.value } }))}
                                  style={{ width: '100%', border: '1.5px solid var(--color-border)', borderRadius: 7, padding: '6px 10px', fontSize: '0.82rem', fontFamily: 'var(--font-sans)', outline: 'none' }} />
                              </div>
                              <div>
                                <label style={{ display: 'block', fontSize: '0.68rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--color-text-muted)', marginBottom: 5 }}>Departure Time</label>
                                <input type="time" value={times[e._id]?.departure || ''}
                                  onChange={e2 => setTimes(t => ({ ...t, [e._id]: { ...t[e._id], departure: e2.target.value } }))}
                                  style={{ width: '100%', border: '1.5px solid var(--color-border)', borderRadius: 7, padding: '6px 10px', fontSize: '0.82rem', fontFamily: 'var(--font-sans)', outline: 'none' }} />
                              </div>
                            </>
                          ) : (
                            <>
                              <div>
                                <label style={{ display: 'block', fontSize: '0.68rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--color-text-muted)', marginBottom: 5 }}>Check-In</label>
                                <input type="time" value={times[e._id]?.checkIn || ''}
                                  onChange={e2 => setTimes(t => ({ ...t, [e._id]: { ...t[e._id], checkIn: e2.target.value } }))}
                                  style={{ width: '100%', border: '1.5px solid var(--color-border)', borderRadius: 7, padding: '6px 10px', fontSize: '0.82rem', fontFamily: 'var(--font-sans)', outline: 'none' }} />
                              </div>
                              <div>
                                <label style={{ display: 'block', fontSize: '0.68rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--color-text-muted)', marginBottom: 5 }}>Check-Out</label>
                                <input type="time" value={times[e._id]?.checkOut || ''}
                                  onChange={e2 => setTimes(t => ({ ...t, [e._id]: { ...t[e._id], checkOut: e2.target.value } }))}
                                  style={{ width: '100%', border: '1.5px solid var(--color-border)', borderRadius: 7, padding: '6px 10px', fontSize: '0.82rem', fontFamily: 'var(--font-sans)', outline: 'none' }} />
                              </div>
                            </>
                          )}
                          <div style={{ gridColumn: 'span 2' }}>
                            <label style={{ display: 'block', fontSize: '0.68rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--color-text-muted)', marginBottom: 5 }}>Remarks</label>
                            <input value={remarks[e._id] || ''} placeholder="Optional note..."
                              onChange={e2 => setRemarks(r => ({ ...r, [e._id]: e2.target.value }))}
                              style={{ width: '100%', border: '1.5px solid var(--color-border)', borderRadius: 7, padding: '6px 10px', fontSize: '0.82rem', fontFamily: 'var(--font-sans)', outline: 'none' }} />
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>

        {entityList.length === 0 && (
          <div style={{ textAlign: 'center', padding: '40px 20px', color: 'var(--color-text-muted)', fontSize: '0.875rem' }}>
            No active {isTeacher ? 'teachers' : 'volunteers'} found.
          </div>
        )}
      </div>

      {entityList.length > 0 && (
        <div style={{ marginTop: 14, display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
          <button className="btn btn-secondary" onClick={() => setStatuses({})}>
            <RefreshCw size={14} /> Reset
          </button>
          <button className="btn btn-primary" onClick={handleSubmit} disabled={submitMutation.isPending}>
            {submitMutation.isPending
              ? <><div className="spinner" style={{ width: 14, height: 14, borderWidth: 2 }} /> Saving...</>
              : <><Save size={14} /> Save {markedCount > 0 ? `(${markedCount})` : 'All'}</>}
          </button>
        </div>
      )}
    </div>
  );
}

// ─── Admin: Manage Student Attendance ─────────────────────────────
function AdminStudentAttendance() {
  const { user } = useAuth();
  const { vbsYear } = useActiveYear();
  const [dateFilter, setDateFilter] = useState(getTodayIST());
  const [modifyRecord, setModifyRecord] = useState(null);
  const [historyRecord, setHistoryRecord] = useState(null);
  const [page, setPage] = useState(1);
  const qc = useQueryClient();

  const { data: activeSettings } = useQuery({
    queryKey: ['active-settings'],
    queryFn: () => settingsAPI.getActive().then(r => r.data?.data),
  });

  const { data: allClasses } = useQuery({
    queryKey: ['classes', vbsYear],
    queryFn: () => classesAPI.getAll({ year: vbsYear }),
    select: d => d.data?.data || [],
    enabled: !!vbsYear,
  });

  const { data: records, isLoading, isFetching, refetch } = useQuery({
    queryKey: ['admin-student-attendance', dateFilter, vbsYear],
    queryFn: () => attendanceAPI.getStudentAttendance({ date: dateFilter, vbsYear }).then(r => r.data?.data || []),
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => attendanceAPI.deleteStudentAttendance(id),
    onSuccess: () => { toast.success('Record deleted'); qc.invalidateQueries(['admin-student-attendance']); },
    onError: (err) => toast.error(err.response?.data?.message || 'Failed'),
  });

  const classStudentCountMap = React.useMemo(() => {
    const map = {};
    (allClasses || []).forEach(c => { map[c._id?.toString()] = c.studentCount || 0; });
    return map;
  }, [allClasses]);

  const pageSize  = 20;
  const allRecords = records || [];
  const paged     = allRecords.slice((page - 1) * pageSize, page * pageSize);
  const pages     = Math.ceil(allRecords.length / pageSize);
  const totalPresent = allRecords.reduce((sum, r) => sum + (r.records?.filter(x => x.status === 'present').length || 0), 0);
  const totalAbsent  = allRecords.reduce((sum, r) => sum + (r.records?.filter(x => x.status === 'absent').length || 0), 0);
  const overallRate  = (totalPresent + totalAbsent) > 0 ? Math.round((totalPresent / (totalPresent + totalAbsent)) * 100) : 0;

  return (
    <div>
      <div style={{ display: 'flex', gap: 10, marginBottom: 16, alignItems: 'flex-end', flexWrap: 'wrap' }}>
        <div style={{ flex: '1 1 180px', minWidth: 180 }}>
          <DateInput label="Date" value={dateFilter} onChange={(v) => { setDateFilter(v); setPage(1); }}
            vbsStartDate={activeSettings?.dates?.startDate?.slice(0, 10)}
            vbsEndDate={activeSettings?.dates?.endDate?.slice(0, 10)}
            showVBSDays={true} />
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', paddingBottom: 1 }}>
          <button className="btn btn-secondary btn-sm" onClick={() => refetch()}>
            <RefreshCw size={14} className={isFetching ? 'spin' : ''} />
          </button>
        </div>
      </div>

      <PendingClassesPanel date={dateFilter} vbsYear={vbsYear} classes={allClasses} submittedRecords={allRecords} />

      {allRecords.length > 0 && (
        <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
          {[
            { label: 'Classes',  value: allRecords.length, color: '#3b82f6' },
            { label: 'Present',  value: totalPresent, color: '#16a34a' },
            { label: 'Absent',   value: totalAbsent, color: '#dc2626' },
            { label: 'Rate',     value: `${overallRate}%`, color: overallRate >= 80 ? '#16a34a' : overallRate >= 60 ? '#d97706' : '#dc2626' },
          ].map(s => (
            <div key={s.label} className="stat-card" style={{ padding: '12px 18px', flex: '0 0 auto' }}>
              <div className="stat-label">{s.label}</div>
              <div style={{ fontSize: '1.4rem', fontWeight: 800, color: s.color }}>{s.value}</div>
            </div>
          ))}
        </div>
      )}

      {isLoading ? (
        <div className="loading-center"><div className="spinner" /></div>
      ) : (
        <div className="card">
          <div className="card-header">
            <span className="card-title">Student Attendance — {formatDisplayDate(dateFilter)}</span>
            <span style={{ fontSize: '0.78rem', color: 'var(--color-text-muted)' }}>{allRecords.length} submitted</span>
          </div>
          {allRecords.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 40, color: 'var(--color-text-secondary)', fontSize: '0.875rem' }}>
              No attendance submitted for this date yet.
            </div>
          ) : (
            <>
              <div className="table-container">
                <table>
                  <thead>
                    <tr>
                      <th>Class</th><th>Category</th><th>Submitted By</th>
                      <th style={{ textAlign: 'center' }}>Present</th>
                      <th style={{ textAlign: 'center' }}>Absent</th>
                      <th>Rate</th><th>Flag</th>
                      <th style={{ width: 110 }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paged.map(rec => {
                      const present = rec.records?.filter(r => r.status === 'present').length || 0;
                      const absent  = rec.records?.filter(r => r.status === 'absent').length || 0;
                      const total   = present + absent;
                      const rate    = total > 0 ? Math.round((present / total) * 100) : 0;

                      const classId = rec.class?._id?.toString();
                      const liveCount = classStudentCountMap[classId] ?? null;
                      const recordedCount = rec.records?.length ?? 0;
                      const hasNewStudents = liveCount !== null && liveCount > recordedCount;

                      return (
                        <tr key={rec._id} style={{ background: hasNewStudents ? 'linear-gradient(135deg, #fffbeb, white)' : undefined }}>
                          <td>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                              <span style={{ fontWeight: 600 }}>{rec.class?.name}</span>
                              {hasNewStudents && (
                                <span style={{
                                  display: 'inline-flex', alignItems: 'center', gap: 3,
                                  padding: '1px 7px', borderRadius: 99,
                                  background: '#fef3c7', border: '1px solid #fbbf24',
                                  fontSize: '0.62rem', fontWeight: 800, color: '#92400e',
                                }}>
                                  <UserPlus size={9} /> {liveCount - recordedCount} New
                                </span>
                              )}
                            </div>
                          </td>
                          <td><span className={`badge cat-${rec.class?.category}`}>{rec.class?.category}</span></td>
                          <td style={{ color: 'var(--color-text-secondary)', fontSize: '0.8rem' }}>{rec.submittedByName}</td>
                          <td style={{ textAlign: 'center' }}><span style={{ color: '#16a34a', fontWeight: 700 }}>{present}</span></td>
                          <td style={{ textAlign: 'center' }}><span style={{ color: '#dc2626', fontWeight: 700 }}>{absent}</span></td>
                          <td><RateBar rate={rate} /></td>
                          <td>{rec.isModified ? <span className="badge badge-orange">Modified</span> : <span className="badge badge-green">Original</span>}</td>
                          <td>
                            <div style={{ display: 'flex', gap: 4 }}>
                              {user.role === 'admin' && <button className="btn btn-secondary btn-icon btn-sm" onClick={() => setModifyRecord(rec)}><Edit2 size={13} /></button>}
                              <button className="btn btn-secondary btn-icon btn-sm" onClick={() => setHistoryRecord(rec)}><History size={13} /></button>
                              {user.role === 'admin' && (
                                <button className="btn btn-ghost btn-icon btn-sm" style={{ color: 'var(--color-danger)' }}
                                  onClick={() => window.confirm('Delete record?') && deleteMutation.mutate(rec._id)}>
                                  <Trash2 size={13} />
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {pages > 1 && (
                <div className="pagination">
                  <span className="page-info">{allRecords.length} records</span>
                  <div className="page-btns">
                    <button className="btn btn-secondary btn-sm" onClick={() => setPage(p => p - 1)} disabled={page <= 1}><ChevronLeft size={14} /></button>
                    {Array.from({ length: pages }, (_, i) => i + 1).map(p => (
                      <button key={p} className={`btn btn-sm ${p === page ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setPage(p)} style={{ minWidth: 32, justifyContent: 'center' }}>{p}</button>
                    ))}
                    <button className="btn btn-secondary btn-sm" onClick={() => setPage(p => p + 1)} disabled={page >= pages}><ChevronRight size={14} /></button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}
      {modifyRecord  && <ModifyModal  record={modifyRecord}  onClose={() => setModifyRecord(null)} />}
      {historyRecord && <HistoryModal record={historyRecord} onClose={() => setHistoryRecord(null)} />}
    </div>
  );
}

// ─── Modify Modal — FIXED: includes new students added after submission ──
function ModifyModal({ record, onClose }) {
  const qc = useQueryClient();
  const [changes, setChanges] = useState({});
  const [reason, setReason] = useState('');

  // Fetch the live class student list so newly-added students appear
  const { data: liveClassData, isLoading: loadingClass } = useQuery({
    queryKey: ['class-full-modify', record.class?._id],
    queryFn: () => classesAPI.getOne(record.class?._id).then(r => r.data?.data),
    enabled: !!record.class?._id,
  });

  // Map of already-recorded student IDs → record entry
  const recordedMap = {};
  (record.records || []).forEach(r => {
    const sid = r.student?._id?.toString() || r.student?.toString();
    if (sid) recordedMap[sid] = r;
  });

  // Students in the live class that are NOT in the submitted record
  const liveStudents = liveClassData?.students || [];
  const newStudents = liveStudents.filter(s => !recordedMap[s._id?.toString()]);

  const modifyMutation = useMutation({
    mutationFn: (data) => attendanceAPI.modifyStudentAttendance(record._id, data),
    onSuccess: () => {
      toast.success('Attendance modified — audit saved');
      qc.invalidateQueries(['admin-student-attendance']);
      onClose();
    },
    onError: (err) => toast.error(err.response?.data?.message || 'Failed'),
  });

  const totalChanges = Object.keys(changes).length;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal modal-lg" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <div>
            <span style={{ fontWeight: 700 }}>Edit Attendance — {record.class?.name}</span>
            <div style={{ fontSize: '0.72rem', color: 'var(--color-text-secondary)', marginTop: 2 }}>
              {formatDisplayDate(record.date)} · By {record.submittedByName}
            </div>
          </div>
          <button className="btn btn-ghost btn-icon" onClick={onClose}><X size={18} /></button>
        </div>

        <div className="modal-body">
          <div className="alert alert-warning" style={{ marginBottom: 16 }}>
            <AlertTriangle size={14} style={{ flexShrink: 0 }} />
            <div>All changes are permanently logged in the audit trail.</div>
          </div>

          {loadingClass ? (
            <div className="loading-center" style={{ padding: 32 }}><div className="spinner" /></div>
          ) : (
            <div className="table-container" style={{ marginBottom: 16 }}>
              <table>
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Name</th>
                    <th>Current Status</th>
                    <th>Change To</th>
                  </tr>
                </thead>
                <tbody>
                  {/* ── Already-recorded students ── */}
                  {(record.records || []).map(r => {
                    const sid = r.student?._id?.toString() || r.student?.toString();
                    const newStatus = changes[sid];
                    return (
                      <tr key={sid} style={{ background: newStatus ? '#fffbeb' : undefined }}>
                        <td>
                          <span className="code" style={{ fontSize: '0.72rem' }}>{r.student?.studentId || '—'}</span>
                        </td>
                        <td style={{ fontWeight: newStatus ? 700 : 500 }}>{r.student?.name || '—'}</td>
                        <td><StatusBadge status={r.status} /></td>
                        <td>
                          <div style={{ display: 'flex', gap: 6 }}>
                            {['present', 'absent'].map(s => (
                              <button key={s} onClick={() => {
                                if (s !== r.status) setChanges(c => ({ ...c, [sid]: s }));
                                else { const c = { ...changes }; delete c[sid]; setChanges(c); }
                              }}
                                style={{
                                  padding: '3px 12px', borderRadius: 6, cursor: 'pointer',
                                  fontSize: '0.75rem', fontWeight: 600,
                                  border: `1.5px solid ${changes[sid] === s ? (s === 'present' ? '#16a34a' : '#dc2626') : 'var(--color-border)'}`,
                                  background: changes[sid] === s ? (s === 'present' ? '#16a34a' : '#dc2626') : 'white',
                                  color: changes[sid] === s ? 'white' : 'var(--color-text-secondary)',
                                }}>
                                {s === 'present' ? '✓ P' : '✗ A'}
                              </button>
                            ))}
                          </div>
                        </td>
                      </tr>
                    );
                  })}

                  {/* ── New students: added to class after submission ── */}
                  {newStudents.length > 0 && (
                    <tr>
                      <td colSpan={4} style={{
                        padding: '10px 14px',
                        background: 'linear-gradient(135deg, #fef9f0, #fefce8)',
                        borderTop: '2px solid #fbbf24',
                        borderBottom: '1px solid #fde68a',
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                          <UserPlus size={14} color="#d97706" />
                          <span style={{ fontSize: '0.75rem', fontWeight: 800, color: '#92400e' }}>
                            {newStudents.length} student{newStudents.length > 1 ? 's' : ''} added to this class after submission — select their status below
                          </span>
                        </div>
                      </td>
                    </tr>
                  )}
                  {newStudents.map(s => {
                    const sid = s._id?.toString();
                    const selectedStatus = changes[sid];
                    return (
                      <tr key={sid} style={{
                        background: selectedStatus
                          ? (selectedStatus === 'present' ? 'rgba(22,163,74,0.05)' : 'rgba(220,38,38,0.05)')
                          : 'linear-gradient(135deg, #fefce8, #fef9f0)',
                        borderLeft: '3px solid #fbbf24',
                      }}>
                        <td>
                          <span className="code" style={{ fontSize: '0.72rem' }}>{s.studentId || '—'}</span>
                        </td>
                        <td>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <span style={{ fontWeight: 700 }}>{s.name}</span>
                            <span style={{
                              display: 'inline-flex', alignItems: 'center', gap: 3,
                              padding: '1px 7px', borderRadius: 99,
                              background: '#fef3c7', border: '1px solid #fbbf24',
                              fontSize: '0.6rem', fontWeight: 800, color: '#92400e',
                            }}>
                              ✦ NEW
                            </span>
                          </div>
                          <div style={{ fontSize: '0.7rem', color: 'var(--color-text-muted)', marginTop: 1 }}>
                            {['PreKG', 'LKG', 'UKG'].includes(s.grade) ? s.grade : `Std ${s.grade}`}
                          </div>
                        </td>
                        <td>
                          <span style={{
                            display: 'inline-flex', alignItems: 'center', gap: 4,
                            padding: '3px 9px', borderRadius: 99,
                            background: '#fef3c7', color: '#92400e',
                            fontSize: '0.7rem', fontWeight: 700,
                          }}>
                            ⚠ Not Yet Recorded
                          </span>
                        </td>
                        <td>
                          <div style={{ display: 'flex', gap: 6 }}>
                            {['present', 'absent'].map(st => (
                              <button key={st} onClick={() => {
                                if (selectedStatus === st) {
                                  const c = { ...changes }; delete c[sid]; setChanges(c);
                                } else {
                                  setChanges(c => ({ ...c, [sid]: st }));
                                }
                              }}
                                style={{
                                  padding: '3px 12px', borderRadius: 6, cursor: 'pointer',
                                  fontSize: '0.75rem', fontWeight: 600,
                                  border: `1.5px solid ${selectedStatus === st ? (st === 'present' ? '#16a34a' : '#dc2626') : 'var(--color-border)'}`,
                                  background: selectedStatus === st ? (st === 'present' ? '#16a34a' : '#dc2626') : 'white',
                                  color: selectedStatus === st ? 'white' : 'var(--color-text-secondary)',
                                }}>
                                {st === 'present' ? '✓ P' : '✗ A'}
                              </button>
                            ))}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          <div className="form-group">
            <label className="form-label">Reason (recommended)</label>
            <textarea
              className="form-textarea"
              rows={2}
              value={reason}
              onChange={e => setReason(e.target.value)}
              placeholder="e.g., Parent confirmed child was present; added late-enrolled students"
            />
          </div>
        </div>

        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button
            className="btn btn-primary"
            disabled={totalChanges === 0 || modifyMutation.isPending}
            onClick={() => modifyMutation.mutate({
              changes: Object.entries(changes).map(([studentId, newStatus]) => ({ studentId, newStatus })),
              reason,
            })}
          >
            <Save size={15} /> Save {totalChanges > 0 ? `${totalChanges} Change(s)` : ''}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── History Modal ─────────────────────────────────────────────────
function HistoryModal({ record, onClose }) {
  const history = record.modificationHistory || [];
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal modal-lg" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <div>
            <span style={{ fontWeight: 700 }}>Modification History</span>
            <div style={{ fontSize: '0.72rem', color: 'var(--color-text-secondary)', marginTop: 2 }}>{record.class?.name} — {formatDisplayDate(record.date)}</div>
          </div>
          <button className="btn btn-ghost btn-icon" onClick={onClose}><X size={18} /></button>
        </div>
        <div className="modal-body">
          <div style={{ padding: '10px 14px', background: '#f0fdf4', borderRadius: 10, border: '1px solid #bbf7d0', marginBottom: 14, fontSize: '0.82rem' }}>
            <div style={{ fontWeight: 700, color: '#15803d', marginBottom: 2 }}>✓ Original Submission</div>
            <div style={{ color: 'var(--color-text-secondary)' }}>
              By <strong>{record.submittedByName}</strong> ({record.submittedByRole}) ·{' '}
              {record.createdAt ? new Date(record.createdAt).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true }) : '—'}
            </div>
          </div>
          {history.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 32, color: 'var(--color-text-muted)' }}>No modifications yet.</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {history.map((mod, i) => (
                <div key={i} style={{ border: '1px solid var(--color-border)', borderRadius: 12, padding: 14 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8, flexWrap: 'wrap', gap: 6 }}>
                    <span style={{ fontWeight: 700, fontSize: '0.85rem' }}>Modification #{i + 1} by {mod.modifiedByName}</span>
                    <span style={{ fontSize: '0.72rem', color: 'var(--color-text-muted)' }}>
                      {new Date(mod.modifiedAt).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true })}
                    </span>
                  </div>
                  {mod.reason && mod.reason !== 'No reason specified' && (
                    <div style={{ fontSize: '0.8rem', color: 'var(--color-text-secondary)', marginBottom: 8, fontStyle: 'italic', padding: '6px 10px', background: 'var(--color-bg)', borderRadius: 7 }}>
                      "{mod.reason}"
                    </div>
                  )}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    {mod.changes?.map((c, j) => (
                      <div key={j} style={{ fontSize: '0.8rem', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                        <span style={{ fontWeight: 600, minWidth: 120 }}>{c.entityName || '—'}</span>
                        <StatusBadge status={c.previousStatus} />
                        <span style={{ color: 'var(--color-text-muted)' }}>→</span>
                        <StatusBadge status={c.newStatus} />
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}

// ─── Admin: Submit On Behalf ───────────────────────────────────────
function AdminSubmitOnBehalf() {
  const { vbsYear } = useActiveYear();
  const qc = useQueryClient();
  const [date, setDate] = useState(getTodayIST());
  const [selectedClassId, setSelectedClassId] = useState('');
  const [records, setRecords] = useState({});
  const todayIST = getTodayIST();

  const { data: activeSettings } = useQuery({
    queryKey: ['active-settings'],
    queryFn: () => settingsAPI.getActive().then(r => r.data?.data),
  });

  const { data: classes } = useQuery({
    queryKey: ['classes', vbsYear],
    queryFn: () => classesAPI.getAll({ year: vbsYear }),
    select: d => d.data?.data || [],
    enabled: !!vbsYear,
  });

  const { data: classData } = useQuery({
    queryKey: ['class-full', selectedClassId],
    queryFn: () => classesAPI.getOne(selectedClassId).then(r => r.data?.data),
    enabled: !!selectedClassId,
  });

  const { data: existingRecord } = useQuery({
    queryKey: ['attendance-check-admin', date, selectedClassId],
    queryFn: () => attendanceAPI.getStudentAttendance({ date, classId: selectedClassId }).then(r => r.data?.data?.[0]),
    enabled: !!selectedClassId,
  });

  const submitMutation = useMutation({
    mutationFn: (data) => attendanceAPI.submitStudentAttendance(data),
    onSuccess: () => {
      toast.success('Attendance submitted');
      qc.invalidateQueries(['attendance-check-admin']);
      qc.invalidateQueries(['admin-student-attendance']);
      setRecords({});
    },
    onError: (err) => toast.error(err.response?.data?.message || 'Failed'),
  });

  const students = classData?.students || [];
  const alreadySubmitted = !!existingRecord;
  const markAll = (status) => { const all = {}; students.forEach(s => { all[s._id] = status; }); setRecords(all); };
  const presentCount = Object.values(records).filter(v => v === 'present').length;
  const markedCount  = Object.keys(records).length;

  return (
    <div>
      <div className="alert alert-info" style={{ marginBottom: 16 }}>
        <Info size={15} style={{ flexShrink: 0 }} />
        <div>Submit student attendance on behalf — no time window restrictions for admin.</div>
      </div>
      <div style={{ display: 'flex', gap: 12, marginBottom: 16, alignItems: 'flex-end', flexWrap: 'wrap' }}>
        <div style={{ flex: '1 1 180px', minWidth: 180 }}>
          <DateInput label="Date" value={date} onChange={(v) => { setDate(v); setRecords({}); }}
            vbsStartDate={activeSettings?.dates?.startDate?.slice(0, 10)}
            vbsEndDate={activeSettings?.dates?.endDate?.slice(0, 10)}
            showVBSDays={true} />
        </div>
        <div style={{ flex: '1 1 200px', minWidth: 180 }}>
          <label className="form-label">Class</label>
          <select className="form-select" value={selectedClassId} onChange={e => { setSelectedClassId(e.target.value); setRecords({}); }}>
            <option value="">Select a class...</option>
            {(classes || []).map(c => <option key={c._id} value={c._id}>{c.name} ({c.category})</option>)}
          </select>
        </div>
        {students.length > 0 && !alreadySubmitted && (
          <div style={{ display: 'flex', gap: 6 }}>
            <button className="btn btn-secondary btn-sm" onClick={() => markAll('present')}>All P</button>
            <button className="btn btn-secondary btn-sm" onClick={() => markAll('absent')}>All A</button>
          </div>
        )}
      </div>

      {selectedClassId && alreadySubmitted && (
        <>
          <div className="alert alert-warning" style={{ marginBottom: 16 }}>
            <AlertCircle size={15} style={{ flexShrink: 0 }} />
            <div>Already submitted. Use <strong>Manage</strong> tab to modify.</div>
          </div>
          {classData && (
            <NewStudentsAlert existingRecord={existingRecord} currentStudents={students} />
          )}
        </>
      )}

      {selectedClassId && classData && !alreadySubmitted && (
        <div className="card">
          <div className="card-header">
            <span className="card-title">{classData.name} — {formatDisplayDate(date)}</span>
            <div style={{ fontSize: '0.8rem', color: 'var(--color-text-secondary)', display: 'flex', gap: 12 }}>
              <span style={{ color: '#16a34a', fontWeight: 700 }}>{presentCount} P</span>
              <span style={{ color: '#dc2626', fontWeight: 700 }}>{markedCount - presentCount} A</span>
              <span style={{ color: 'var(--color-text-muted)' }}>{students.length - markedCount} left</span>
            </div>
          </div>
          {students.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 32, color: 'var(--color-text-secondary)', fontSize: '0.875rem' }}>
              <Users size={28} style={{ margin: '0 auto 8px', display: 'block', color: 'var(--color-text-muted)' }} />
              No students assigned to this class yet.
            </div>
          ) : (
            <>
              <div className="table-container">
                <table>
                  <thead><tr><th>#</th><th>ID</th><th>Name</th><th>Grade</th><th>Status</th></tr></thead>
                  <tbody>
                    {students.map((s, idx) => (
                      <tr key={s._id}>
                        <td style={{ color: 'var(--color-text-muted)', fontSize: '0.78rem' }}>{idx + 1}</td>
                        <td><span className="code">{s.studentId || '—'}</span></td>
                        <td style={{ fontWeight: 600 }}>{s.name}</td>
                        <td style={{ fontSize: '0.82rem', color: 'var(--color-text-secondary)' }}>
                          {['PreKG', 'LKG', 'UKG'].includes(s.grade) ? s.grade : `Std ${s.grade}`}
                        </td>
                        <td>
                          <div style={{ display: 'flex', gap: 5 }}>
                            {['present', 'absent'].map(st => (
                              <button key={st} onClick={() => setRecords(r => ({ ...r, [s._id]: st }))}
                                style={{
                                  padding: '4px 12px', borderRadius: 7, border: 'none', cursor: 'pointer',
                                  fontWeight: 600, fontSize: '0.78rem', transition: 'all 0.12s',
                                  background: records[s._id] === st ? (st === 'present' ? '#16a34a' : '#dc2626') : 'var(--color-bg)',
                                  color: records[s._id] === st ? 'white' : 'var(--color-text-secondary)',
                                }}>
                                {st === 'present' ? '✓' : '✗'}
                              </button>
                            ))}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div style={{ padding: '14px 18px', borderTop: '1px solid var(--color-border)', display: 'flex', justifyContent: 'flex-end' }}>
                <button className="btn btn-primary" disabled={submitMutation.isPending} onClick={() => {
                  const recs = students.map(s => ({ studentId: s._id, status: records[s._id] || 'absent' }));
                  submitMutation.mutate({ date, classId: selectedClassId, records: recs });
                }}>
                  {submitMutation.isPending ? 'Submitting...' : <><Save size={15} /> Submit</>}
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Editor: Submit Student Attendance ────────────────────────────
function EditorStudentAttendance() {
  const { vbsYear } = useActiveYear();
  const qc = useQueryClient();
  const [date, setDate] = useState(getTodayIST());
  const [selectedClassId, setSelectedClassId] = useState('');
  const [records, setRecords] = useState({});
  const todayIST = getTodayIST();

  const { data: windowData } = useQuery({
    queryKey: ['window-status'],
    queryFn: () => attendanceAPI.getWindowStatus().then(r => r.data?.data),
    refetchInterval: 60000,
  });

  const { data: activeSettings } = useQuery({
    queryKey: ['active-settings'],
    queryFn: () => settingsAPI.getActive().then(r => r.data?.data),
  });

  const { data: classes } = useQuery({
    queryKey: ['classes', vbsYear],
    queryFn: () => classesAPI.getAll({ year: vbsYear }),
    select: d => d.data?.data || [],
    enabled: !!vbsYear,
  });

  const { data: classData } = useQuery({
    queryKey: ['class-full', selectedClassId],
    queryFn: () => classesAPI.getOne(selectedClassId).then(r => r.data?.data),
    enabled: !!selectedClassId,
  });

  const { data: existingRecord, isLoading: checkingExisting } = useQuery({
    queryKey: ['attendance-check-editor', date, selectedClassId],
    queryFn: () => attendanceAPI.getStudentAttendance({ date, classId: selectedClassId }).then(r => r.data?.data?.[0]),
    enabled: !!selectedClassId,
  });

  const submitMutation = useMutation({
    mutationFn: (data) => attendanceAPI.submitStudentAttendance(data),
    onSuccess: () => {
      toast.success('Attendance submitted successfully!');
      qc.invalidateQueries(['attendance-check-editor']);
      setRecords({});
      setSelectedClassId('');
    },
    onError: (err) => toast.error(err.response?.data?.message || 'Submission failed'),
  });

  const students = classData?.students || [];
  const alreadySubmitted = !!existingRecord;
  const isToday = date === todayIST;
  const windowStopped = windowData?.stopped;
  const windowOpen = windowData?.allowed && !windowStopped;
  const canSubmit = isToday && windowOpen && !alreadySubmitted && !!selectedClassId;

  const presentCount = Object.values(records).filter(v => v === 'present').length;
  const markedCount  = Object.keys(records).length;
  const unmarked     = students.length - markedCount;
  const markAll = (status) => { const all = {}; students.forEach(s => { all[s._id] = status; }); setRecords(all); };

  return (
    <div>
      <div style={{
        background: 'linear-gradient(135deg, #eff6ff, #f0f9ff)',
        border: '1px solid #bfdbfe',
        borderRadius: 12, padding: '12px 16px', marginBottom: 16,
        display: 'flex', gap: 10, alignItems: 'flex-start',
      }}>
        <Info size={15} color="#2563eb" style={{ flexShrink: 0, marginTop: 1 }} />
        <div style={{ fontSize: '0.82rem', color: '#1e40af', lineHeight: 1.5 }}>
          <strong>Editor Restrictions:</strong> You can submit attendance for any class within the attendance window.
          Attendance can only be submitted once per class per day — modifications require admin access.
          You can only submit for <strong>today</strong> during the open window.
        </div>
      </div>

      {windowStopped ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px', borderRadius: 10, marginBottom: 16, background: '#fff7ed', border: '1px solid #fed7aa' }}>
          <Ban size={16} color="#ea580c" />
          <div>
            <div style={{ fontWeight: 700, fontSize: '0.845rem', color: '#9a3412' }}>Attendance Window Stopped</div>
            <div style={{ fontSize: '0.78rem', color: '#c2410c', marginTop: 2 }}>{windowData?.message?.replace('⛔ ', '')}</div>
          </div>
        </div>
      ) : (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 12, padding: '10px 16px',
          borderRadius: 10, marginBottom: 16,
          background: windowOpen ? '#f0fdf4' : '#fef2f2',
          border: `1px solid ${windowOpen ? '#bbf7d0' : '#fecaca'}`,
        }}>
          <span style={{ padding: '3px 10px', borderRadius: 99, fontSize: '0.72rem', fontWeight: 800, background: windowOpen ? '#16a34a' : '#dc2626', color: 'white' }}>
            {windowOpen ? 'Window OPEN' : 'Window CLOSED'}
          </span>
          <span style={{ fontSize: '0.82rem', color: 'var(--color-text-secondary)', flex: 1 }}>{windowData?.message || 'Loading...'}</span>
          {windowOpen && windowData?.minutesRemaining > 0 && (
            <span style={{ fontSize: '0.82rem', fontWeight: 700, color: '#92400e', background: '#fef3c7', padding: '3px 10px', borderRadius: 99 }}>
              <Clock size={13} style={{ marginRight: 4, verticalAlign: 'middle' }} />
              {windowData.minutesRemaining} min remaining
            </span>
          )}
        </div>
      )}

      <div style={{ display: 'flex', gap: 12, marginBottom: 16, alignItems: 'flex-end', flexWrap: 'wrap' }}>
        <div style={{ flex: '1 1 180px', minWidth: 180 }}>
          <DateInput
            label="Attendance Date"
            value={date}
            onChange={(v) => { setDate(v); setRecords({}); setSelectedClassId(''); }}
            max={todayIST}
            vbsStartDate={activeSettings?.dates?.startDate?.slice(0, 10)}
            vbsEndDate={activeSettings?.dates?.endDate?.slice(0, 10)}
            showVBSDays={true}
          />
        </div>
        <div style={{ flex: '1 1 200px', minWidth: 180 }}>
          <label className="form-label">Select Class</label>
          <select
            className="form-select"
            value={selectedClassId}
            onChange={e => { setSelectedClassId(e.target.value); setRecords({}); }}
            disabled={!isToday || windowStopped}
          >
            <option value="">Choose a class...</option>
            {(classes || []).map(c => <option key={c._id} value={c._id}>{c.name} ({c.category})</option>)}
          </select>
        </div>
        {canSubmit && students.length > 0 && (
          <div style={{ display: 'flex', gap: 6 }}>
            <button className="btn btn-secondary btn-sm" onClick={() => markAll('present')}><Check size={14} /> All Present</button>
            <button className="btn btn-secondary btn-sm" onClick={() => markAll('absent')}><X size={14} /> All Absent</button>
            <button className="btn btn-ghost btn-sm" onClick={() => setRecords({})}><RefreshCw size={14} /></button>
          </div>
        )}
      </div>

      {!isToday && (
        <div className="alert alert-warning" style={{ marginBottom: 16 }}>
          <AlertCircle size={15} style={{ flexShrink: 0 }} />
          <div>You can only submit attendance for <strong>today</strong>. Select today's date to proceed.</div>
        </div>
      )}

      {selectedClassId && alreadySubmitted && !checkingExisting && (
        <div className="alert alert-info" style={{ marginBottom: 16 }}>
          <CheckSquare size={15} style={{ flexShrink: 0 }} />
          <div>
            Attendance for <strong>{classData?.name}</strong> on <strong>{formatDisplayDate(date)}</strong> has already been submitted by <strong>{existingRecord?.submittedByName}</strong>.
            To modify it, contact an admin.
          </div>
        </div>
      )}

      {selectedClassId && classData && (
        <div className="card">
          <div className="card-header">
            <div>
              <span className="card-title">{classData.name}</span>
              <span style={{ marginLeft: 8, fontSize: '0.78rem', color: 'var(--color-text-secondary)' }}>
                {classData.category} · {students.length} students
              </span>
            </div>
            {markedCount > 0 && canSubmit && (
              <div style={{ display: 'flex', gap: 10, fontSize: '0.82rem' }}>
                <span style={{ color: '#16a34a', fontWeight: 700 }}>✓ {presentCount} P</span>
                <span style={{ color: '#dc2626', fontWeight: 700 }}>✗ {markedCount - presentCount} A</span>
                {unmarked > 0 && <span style={{ color: 'var(--color-text-muted)' }}>{unmarked} left</span>}
              </div>
            )}
          </div>

          {students.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 32, color: 'var(--color-text-secondary)', fontSize: '0.875rem' }}>
              <Users size={28} style={{ margin: '0 auto 8px', display: 'block', color: 'var(--color-text-muted)' }} />
              No students assigned to this class yet.
            </div>
          ) : (
            <>
              <div className="table-container">
                <table style={{ minWidth: 380 }}>
                  <thead>
                    <tr>
                      <th style={{ width: 36 }}>#</th>
                      <th>Name</th>
                      <th style={{ width: 70 }}>Grade</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {students.map((s, idx) => {
                      const submittedStatus = existingRecord?.records?.find(
                        r => r.student?._id?.toString() === s._id?.toString() || r.student?.toString() === s._id?.toString()
                      )?.status;
                      const currentStatus = alreadySubmitted ? submittedStatus : records[s._id];

                      return (
                        <tr key={s._id} style={{
                          background: canSubmit && records[s._id] ? 'rgba(26,47,94,0.02)' : undefined,
                        }}>
                          <td style={{ color: 'var(--color-text-muted)', fontSize: '0.78rem' }}>{idx + 1}</td>
                          <td style={{ fontWeight: 600, fontSize: '0.875rem' }}>{s.name}</td>
                          <td style={{ fontSize: '0.82rem', color: 'var(--color-text-secondary)' }}>
                            {['PreKG', 'LKG', 'UKG'].includes(s.grade) ? s.grade : `Std ${s.grade}`}
                          </td>
                          <td>
                            {alreadySubmitted ? (
                              <StatusBadge status={currentStatus} />
                            ) : canSubmit ? (
                              <div style={{ display: 'flex', gap: 5 }}>
                                {['present', 'absent'].map(st => (
                                  <button key={st} onClick={() => setRecords(r => ({ ...r, [s._id]: st }))}
                                    style={{
                                      padding: '4px 10px', borderRadius: 7, border: 'none', cursor: 'pointer',
                                      fontWeight: 700, fontSize: '0.75rem', transition: 'all 0.12s',
                                      background: currentStatus === st ? (st === 'present' ? '#16a34a' : '#dc2626') : 'var(--color-bg)',
                                      color: currentStatus === st ? 'white' : 'var(--color-text-secondary)',
                                    }}>
                                    {st === 'present' ? '✓' : '✗'}
                                  </button>
                                ))}
                              </div>
                            ) : (
                              <span style={{ color: 'var(--color-text-muted)', fontSize: '0.78rem' }}>
                                {!isToday ? 'Today only' : windowStopped ? 'Window stopped' : !windowOpen ? 'Window closed' : '—'}
                              </span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {canSubmit && (
                <div style={{ padding: '12px 16px', borderTop: '1px solid var(--color-border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10 }}>
                  <div style={{ fontSize: '0.8rem', color: 'var(--color-text-secondary)' }}>
                    {unmarked > 0
                      ? <span style={{ color: '#d97706' }}>⚠️ {unmarked} unmarked → will be marked Absent</span>
                      : <span style={{ color: '#16a34a' }}>✓ All {students.length} marked</span>
                    }
                  </div>
                  <button
                    className="btn btn-primary"
                    onClick={() => {
                      const recs = students.map(s => ({ studentId: s._id, status: records[s._id] || 'absent' }));
                      submitMutation.mutate({ date, classId: selectedClassId, records: recs });
                    }}
                    disabled={submitMutation.isPending}
                  >
                    {submitMutation.isPending
                      ? <><div className="spinner" style={{ width: 14, height: 14, borderWidth: 2 }} /> Submitting...</>
                      : <><Save size={15} /> Submit Attendance</>
                    }
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

// ─── MAIN ATTENDANCE PAGE ──────────────────────────────────────────
export default function AttendancePage({ initialTab }) {
  const { user } = useAuth();

  const { data: activeSettings } = useQuery({
    queryKey: ['active-settings'],
    queryFn: () => settingsAPI.getActive().then(r => r.data?.data),
  });

  const tabsByRole = {
    admin: [
      { id: 'manage',           label: '📋 Student Records' },
      { id: 'submit-behalf',    label: '✏️ Submit (Admin)' },
      { id: 'teachers',         label: '👩‍🏫 Teacher Attendance' },
      { id: 'volunteers',       label: '🤝 Volunteer Attendance' },
      { id: 'teacher-records',  label: '📊 Teacher Records' },
      { id: 'volunteer-records',label: '📊 Volunteer Records' },
    ],
    editor: [
      { id: 'submit-student', label: '✏️ Submit Student Attendance' },
    ],
    viewer: [
      { id: 'manage',           label: '📋 Student Records' },
      { id: 'teacher-records',  label: '📊 Teacher Records' },
      { id: 'volunteer-records',label: '📊 Volunteer Records' },
    ],
    teacher: [
      { id: 'submit',        label: '✏️ Mark Attendance' },
      { id: 'history',       label: '📅 Submission History' },
      { id: 'my-attendance', label: '👤 My Attendance' },
    ],
  };

  const tabs = tabsByRole[user.role] || [];
  const getDefaultTab = () => {
    if (initialTab === 'my-attendance') return 'my-attendance';
    if (initialTab === 'submit') return 'submit';
    return tabs[0]?.id;
  };
  const [activeTab, setActiveTab] = useState(getDefaultTab);

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Attendance</h1>
          <p className="page-subtitle">
            {user.role === 'teacher'
              ? 'Mark attendance and view your records'
              : user.role === 'editor'
              ? 'Submit student attendance within the open window'
              : 'Manage daily attendance'}
          </p>
        </div>
      </div>

      {['admin', 'teacher'].includes(user.role) && (
        <WindowBanner
          user={user}
          activeSettingsId={activeSettings?._id}
          activeSettings={activeSettings}
        />
      )}

      {tabs.length > 1 && (
        <div style={{ display: 'flex', gap: 4, marginBottom: 20, overflowX: 'auto', paddingBottom: 4, scrollbarWidth: 'none' }}>
          {tabs.map(tab => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)}
              style={{
                padding: '8px 14px', borderRadius: 10,
                border: `1.5px solid ${activeTab === tab.id ? 'var(--color-primary)' : 'var(--color-border)'}`,
                background: activeTab === tab.id ? 'var(--color-primary)' : 'white',
                color: activeTab === tab.id ? 'white' : 'var(--color-text-secondary)',
                cursor: 'pointer', fontFamily: 'var(--font-sans)', fontWeight: 600,
                fontSize: '0.82rem', whiteSpace: 'nowrap', transition: 'all 0.15s',
              }}>
              {tab.label}
            </button>
          ))}
        </div>
      )}

      {activeTab === 'submit'         && <TeacherMarkAttendance />}
      {activeTab === 'history'        && <TeacherAttendanceHistory />}
      {activeTab === 'my-attendance'  && <MyOwnAttendanceRecords />}

      {activeTab === 'manage'         && <AdminStudentAttendance />}
      {activeTab === 'submit-behalf'  && <AdminSubmitOnBehalf />}
      {activeTab === 'teachers'       && <StaffAttendancePanel type="teacher" />}
      {activeTab === 'volunteers'     && <StaffAttendancePanel type="volunteer" />}
      {activeTab === 'teacher-records'  && <TeacherAttendanceRecords />}
      {activeTab === 'volunteer-records' && <VolunteerAttendanceRecords />}

      {activeTab === 'submit-student' && <EditorStudentAttendance />}
    </div>
  );
}
